import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import classNames from 'classnames';
import { useKeenSlider } from 'keen-slider/react';

import * as selectors from '../accountsSelector';

import DashboardLoanHeader from '../../sharedDashboardComponents/DashboardLoanHeader';
import LoanCost from '../dashboardComponents/loanCost/LoanCost';
import InterestAccrued from './loanInformation/InterestAccrued';
import OutstandingBalance from './loanInformation/OutstandingBalance';
import CurrentPrincipal from './loanInformation/CurrentPrincipal';
import CreditLimit from './loanInformation/CreditLimit';
import LoanProgressBar from '../../sharedDashboardComponents/LoanProgressBar/LoanProgressBar';
import DashboardActions from './dashboardActions/DashboardActions';
import DashboardActionPanel from './dashboardActions/DashboardActionPanel';
import FAQs from '../../sharedDashboardComponents/FAQs/FAQs';

import styles from './OutstandingView.module.css';
import questions from './OutstandingView.faqs';
import { RootState } from '@iwProvider/configureStore';
import {
    DashboardActionTypes,
    TOP_UP_DRAWDOWN_ACTION
} from '../../sharedDashboardComponents/LoanProgressBar/DashboardActions.types';

import { tracking } from '@iwoca/frontend-tracking-library';
import { TProductType } from '../../../myAccount/dashboard/Dashboard.types';
import TopUpEligible from './dashboardActions/TopUpEligible/TopUpEligible';
import { getTopUpStatus } from '../NewOutstandingView/NewOutstandingView.helpers';
import TopUpApproved from './dashboardActions/TopUpApproved/TopUpApproved';
import { differenceInMonths } from 'date-fns';

type OwnProps = { accountId: string; productType: TProductType };

const mapStateToProps = (state: RootState, { productType }: OwnProps) => {
    return {
        outstandingBalance: selectors.getOutstandingTotal(state, productType),
        productType,
        loanDuration: selectors.getLoanDurationByMonth(state, productType),
        topUpStatus: getTopUpStatus(state, productType),
        statusDate: state.api.application.data.status_date,
        declineStatus: state.api.application.data.decline_status
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const OutstandingView = ({
    outstandingBalance,
    accountId,
    productType,
    intl,
    loanDuration,
    topUpStatus,
    statusDate,
    declineStatus
}: PropsFromRedux & WrappedComponentProps & OwnProps) => {
    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: accountId,
            // @ts-expect-error
            product_type: productType,
            dashboard_state: 'ongoing',
            topup_status: topUpStatus
        });
    }, [accountId, productType, topUpStatus]);

    const [activeDashboardAction, setActiveDashboardAction] =
        useState<DashboardActionTypes>(null);
    const [sliderRef, slider] = useKeenSlider({
        initial: 0,
        drag: false // disables phone controls and gestures
    });

    function unsetActiveDashboardAction() {
        setActiveDashboardAction(null);
    }

    const scrollFormIntoView = () => {
        if (slider.current) {
            const topTarget = slider.current?.container.offsetTop;

            window.scrollTo({
                behavior: 'smooth',
                top: topTarget,
                left: 0
            });
        }
    };

    const toggleSliderNext = (action: DashboardActionTypes) => {
        slider.current?.update();
        setActiveDashboardAction(action);
        slider.current?.next();
        scrollFormIntoView();
    };

    const drawdownPanelVisible = Boolean(
        slider.current && activeDashboardAction === 'top-up-drawdown-action'
    );

    const monthsSinceStatusChange = differenceInMonths(new Date(), statusDate);

    let hasDeclineExpired;

    if (
        (declineStatus === 'Soft Declined' && monthsSinceStatusChange >= 3) ||
        (declineStatus === 'Hard Declined' && monthsSinceStatusChange >= 6)
    ) {
        hasDeclineExpired = true;
    }

    const shouldShowTopupApprovedAction =
        topUpStatus === 'TOPUP_APPROVED' ||
        (topUpStatus === 'TOPUP_DECLINED' && hasDeclineExpired);

    const shouldShowDrawdownPanel =
        topUpStatus &&
        [
            'INTEREST_ONLY_EXTENSION_APPROVED',
            'TOPUP_APPROVED',
            'PRE_APPROVED'
        ].includes(topUpStatus);

    return (
        <div className={styles.dashboardContainerWithFAQs}>
            <DashboardLoanHeader
                productType={productType!}
                productInfo={<LoanCost />}
                loanDuration={loanDuration}
                isDynamic={true}
            />

            <div className={styles.loanInformationWrapper}>
                <div>
                    {topUpStatus === 'TOPUP_ELIGIBLE' && (
                        <div className={styles.topUpEligibleContainer}>
                            <TopUpEligible />
                        </div>
                    )}
                    {shouldShowTopupApprovedAction && (
                        <div className={styles.topUpApprovedContainer}>
                            <TopUpApproved
                                onCtaClick={() => {
                                    tracking.ampli.topupApprovedBannerCtaClicked(
                                        { account_id: accountId }
                                    );
                                    toggleSliderNext(TOP_UP_DRAWDOWN_ACTION);
                                }}
                            />
                        </div>
                    )}
                    <div className={styles.loanInformation}>
                        <div
                            className={styles.progressBarContainer}
                            data-testid="loan-info"
                        >
                            <div>
                                <OutstandingBalance
                                    outstandingBalance={outstandingBalance}
                                />
                            </div>
                            <div className={styles.flexColumn}>
                                <div className={styles.flexRow}>
                                    <div className={styles.flexColumn}>
                                        <InterestAccrued />
                                        <CurrentPrincipal />
                                    </div>
                                    <div
                                        className={classNames(
                                            styles.flexColumn,
                                            styles.creditLimit
                                        )}
                                    >
                                        <CreditLimit />
                                    </div>
                                </div>
                                <LoanProgressBar productType={productType} />
                            </div>
                        </div>

                        <div ref={sliderRef} className="keen-slider">
                            <div className="keen-slider__slide">
                                <DashboardActions
                                    accountId={accountId}
                                    setActiveDashboardAction={toggleSliderNext}
                                />
                            </div>

                            {shouldShowDrawdownPanel && (
                                <div
                                    data-testid="drawdown-panel"
                                    className="keen-slider__slide"
                                    aria-hidden={!drawdownPanelVisible}
                                >
                                    <DashboardActionPanel
                                        activeDashboardAction={
                                            activeDashboardAction
                                        }
                                        showAllDashboardCTAs={() => {
                                            unsetActiveDashboardAction();
                                            slider.current?.prev();
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <FAQs questions={questions(intl, loanDuration, productType)} />
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(injectIntl(OutstandingView));
